import { useState, type FC } from 'react';
import { useQuery } from '@tanstack/react-query';
import { loadRegistration, type Registration, registrationQueryKey } from '@/api/registration';
import { type ApiResponseSingle } from '@/api/tenantClient';
import { useTenantApi } from '@/account/hook/useTenantApi';
import { isError } from '@/lib/util';
import ConfirmationPage from './ConfirmationPage';
import { useRegisterRoute } from '@/registration/hook/useRegisterRoute';
import { useUserSession } from '@/registration/hook/useUserSession';
import { useEventSettingsQuery } from '@/registration/hook/useEventSettingsQuery';
import { StandardSpinner } from '@/base/QueryContainer/QueryContainer';

const APPROVED = 'Approved';

const Confirmation: FC = () => {
  const { eventId } = useRegisterRoute();
  const { createTenantApiRequest, isLoading: isApiPreparing } = useTenantApi();
  const { data: eventSetting } = useEventSettingsQuery(eventId);
  const [error, setError] = useState(false);
  const { userSession } = useUserSession();

  const { data, isLoading, error: fetchError } = useQuery<ApiResponseSingle<Registration>, Error>({
    queryKey: [registrationQueryKey, { eventId }, { token: userSession?.authToken }],
    queryFn: async () => {
      if (userSession?.email === undefined) {
        throw new Error('email is undefined');
      }
      return await loadRegistration(createTenantApiRequest)({ email: userSession.email, eventId });
    },
    // refetch every 1s, until error or status = PAID or refetch count <= 15
    refetchInterval: (regData, { state }) => {
      if (state.dataUpdateCount > 15) { // refetch 15 times, stop and throw error
        setError(true);
        return false;
      }
      return (isError(regData?.errors) ||
        (regData?.item?.status === APPROVED)
      ) ? false : 1000;
    },
    enabled: !!userSession?.email && !isApiPreparing
  });

  if (!data) {
    return <StandardSpinner />;
  }

  return (
    <ConfirmationPage data={data?.item} isLoading={isLoading} error={error} fetchError={fetchError} statusToCheck={APPROVED} confirmationMetadata={eventSetting?.pageMetadata?.confirmation} />
  );
};

export default Confirmation;
